import { useState } from "react";

import { Link, useParams } from "react-router-dom";

import {
  useAtualizarConta,
  useBancos,
  useContas,
  useCriarConta,
} from "../../lib/cadastros/hooks";
import type {
  BancoOut,
  ContaCorrenteOut,
} from "../../lib/cadastros/tipos";
import { useTemPermissao } from "../../lib/auth/permissoes";
import { formatarDecimal } from "../../lib/formato/decimal";
import { ErroHttp } from "../../lib/http/erros";
import {
  FormularioConta,
  type DadosContaForm,
} from "../cadastros/FormularioConta";

/*
 * Página de Contas correntes NESTED por empresa (FE-4 CP-B).
 *
 * A empresa vem do PATH (`useParams` → `empresaId`). Lista as contas daquela
 * empresa (`useContas(empresaId)`) com banco resolvido pelo nome (via `useBancos`,
 * caindo no id se o catálogo não trouxer o banco), agência, número, tipo, saldo e
 * status. O `saldo_disponivel` é SEMPRE exibido como STRING formatada
 * (`formatarDecimal`) — NUNCA convertido para `number`.
 *
 * "Nova conta" (gated por `cadastrar_infraestrutura`) abre o `FormularioConta` em
 * criação (dropdown de banco); "Editar" abre o mesmo form em edição. As mutações
 * são fixadas na `empresaId` (o backend isola IDOR por path — `empresa_id` nunca
 * no corpo) e já invalidam a lista no `onSuccess` do hook.
 *
 * Estado de TELA (`useState`, não Zustand): qual formulário está aberto.
 * SÓ tokens CPMH.
 */

const ACAO_CADASTRAR_INFRAESTRUTURA = "cadastrar_infraestrutura";

type FormAberto =
  | { tipo: "criar" }
  | { tipo: "editar"; conta: ContaCorrenteOut }
  | null;

export function PaginaContas(): JSX.Element {
  // A rota é `empresas/:empresaId/contas`. O param é obrigatório pela rota; o
  // fallback `?? null` mantém o hook ocioso (enabled:false) caso ausente.
  const { empresaId } = useParams<{ empresaId: string }>();
  const id = empresaId ?? null;

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-titulo text-2xl text-off-black">
          Contas correntes
        </h1>
        <Link
          to="/cadastros/empresas"
          className="font-corpo text-base text-flame hover:underline"
        >
          ← Voltar para empresas
        </Link>
      </div>

      {id === null ? (
        <p role="alert" className="font-corpo text-base text-flame">
          Empresa não informada.
        </p>
      ) : (
        <ListaContas empresaId={id} />
      )}
    </section>
  );
}

interface ListaContasProps {
  empresaId: string;
}

/**
 * Lista + formulários das contas de uma empresa fixada. Componente próprio para
 * que os hooks de contas/mutação só montem com a `empresaId` definida (sem o
 * `null` do `useParams` no caminho de fetch).
 */
function ListaContas({ empresaId }: ListaContasProps): JSX.Element {
  const consulta = useContas(empresaId);
  const consultaBancos = useBancos();
  const criar = useCriarConta(empresaId);
  const atualizar = useAtualizarConta(empresaId);
  const podeCadastrar = useTemPermissao(ACAO_CADASTRAR_INFRAESTRUTURA);

  const [form, setForm] = useState<FormAberto>(null);

  const bancos: BancoOut[] = consultaBancos.data ?? [];

  function salvarCriacao(dados: DadosContaForm): void {
    criar.mutate(
      {
        // SEM empresa_id: vem do path (fixado no hook).
        banco_id: dados.banco_id,
        agencia: dados.agencia,
        numero: dados.numero,
        tipo: dados.tipo,
      },
      { onSuccess: () => setForm(null) },
    );
  }

  function salvarEdicao(conta: ContaCorrenteOut, dados: DadosContaForm): void {
    atualizar.mutate(
      {
        contaId: conta.id,
        // banco_id NÃO entra no PATCH (não editável).
        corpo: {
          agencia: dados.agencia,
          numero: dados.numero,
          tipo: dados.tipo,
          ativa: dados.ativa,
        },
      },
      { onSuccess: () => setForm(null) },
    );
  }

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">Carregando contas…</p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar as contas.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const contas = consulta.data;

  function nomeBanco(bancoId: string): string {
    return bancos.find((banco) => banco.id === bancoId)?.nome ?? bancoId;
  }

  return (
    <div className="flex flex-col gap-4">
      {podeCadastrar && (
        <div>
          <button
            type="button"
            onClick={() => setForm({ tipo: "criar" })}
            className="rounded-md border border-flame bg-flame px-4 py-1 font-subtitulo text-snow"
          >
            Nova conta
          </button>
        </div>
      )}

      {contas.length === 0 ? (
        <p className="font-corpo text-base text-off-black">
          Nenhuma conta cadastrada para esta empresa.
        </p>
      ) : (
        <table className="w-full border-collapse font-corpo text-base text-off-black">
          <thead>
            <tr className="border-b border-platinum text-left">
              <th className="py-2 pr-4 font-subtitulo">Banco</th>
              <th className="py-2 pr-4 font-subtitulo">Agência</th>
              <th className="py-2 pr-4 font-subtitulo">Número</th>
              <th className="py-2 pr-4 font-subtitulo">Tipo</th>
              <th className="py-2 pr-4 font-subtitulo">Saldo disponível</th>
              <th className="py-2 pr-4 font-subtitulo">Ativa</th>
              <th className="py-2 pr-4 font-subtitulo">Ações</th>
            </tr>
          </thead>
          <tbody>
            {contas.map((conta) => (
              <tr key={conta.id} className="border-b border-platinum">
                <td className="py-2 pr-4">{nomeBanco(conta.banco_id)}</td>
                <td className="py-2 pr-4">{conta.agencia}</td>
                <td className="py-2 pr-4">{conta.numero}</td>
                <td className="py-2 pr-4">{conta.tipo}</td>
                {/* Saldo SEMPRE como string formatada — nunca number. */}
                <td className="py-2 pr-4">
                  {formatarDecimal(conta.saldo_disponivel)}
                </td>
                <td className="py-2 pr-4">{conta.ativa ? "Sim" : "Não"}</td>
                <td className="py-2 pr-4">
                  {podeCadastrar && (
                    <button
                      type="button"
                      onClick={() => setForm({ tipo: "editar", conta })}
                      className="text-flame hover:underline"
                    >
                      Editar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {form?.tipo === "criar" && (
        <FormularioConta
          bancos={bancos}
          aoSalvar={salvarCriacao}
          aoCancelar={() => setForm(null)}
          salvando={criar.isPending}
          erro={criar.error}
        />
      )}

      {form?.tipo === "editar" && (
        <FormularioConta
          conta={form.conta}
          bancos={bancos}
          aoSalvar={(dados) => salvarEdicao(form.conta, dados)}
          aoCancelar={() => setForm(null)}
          salvando={atualizar.isPending}
          erro={atualizar.error}
        />
      )}
    </div>
  );
}
